import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SectionHeader } from "../../SectionHeader";
import { PrimaryButton } from "../../Button";
import idIcon from "../../../../assets/svgIcons/id.svg";
import locationIcon from "../../../../assets/svgIcons/live-view.svg";
import SummarySectionHeader from "../../../../components/applicantDetailsTabs/SummarySectionHeader";
// ─── Field name helpers ──────────────────────────────────────────────────────
// Basic:   basicReference{N}<Suffix>    e.g. basicReference1FirstName
// Address: addressReference{N}<Suffix>  e.g. addressReference1AddressLine1

const refBasicField = (n: number | string, suffix: string) =>
  `basicReference${n}${suffix}`;
const refAddressField = (n: number | string, suffix: string) =>
  `addressReference${n}${suffix}`;

const BASIC_SUFFIXES = [
  "FirstName",
  "LastName",
  "EmailID",
  "Mobile",
];

const ADDRESS_SUFFIXES = [
  "AddressLine1",
  "AddressLine2",
  "Locality",
  "Landmark",
  "Area",
  "Pincode",
  "City",
  "State",
  "Country",
];

interface ReferenceDetailsProps {
  fields: any[];
  renderField: (field: any) => React.ReactNode;
  formikRef: any;
  values?: any;
  isSaving?: boolean;
  onSaveAndNext?: (referenceNumber: number) => Promise<boolean | void>;
  pageType?:string;
  expandedReference?: number | false;
  onExpandedChange?: (expanded: number | false) => void;
  hideInternalSaveButton?: boolean;
}

const ReferenceDetails: React.FC<ReferenceDetailsProps> = ({
  fields,
  renderField,
  formikRef,
  values,
  isSaving,
  onSaveAndNext,
  pageType,
  expandedReference,
  onExpandedChange,
  hideInternalSaveButton,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));

  // Detect how many reference groups exist from fields
  const refNums = React.useMemo(() => {
    const nums = new Set<number>();
    fields.forEach((f: any) => {
      const m = String(f?.name || "").match(/^basicReference(\d+)/);
      if (m) nums.add(Number(m[1]));
    });
    return Array.from(nums).sort((a, b) => a - b);
  }, [fields]);

  const displayedRefNums = React.useMemo(() => {
    const defaults = [1, 2];
    const merged = Array.from(new Set([...defaults, ...refNums])).sort((a, b) => a - b);
    return merged.slice(0, 2);
  }, [refNums]);

  const [localExpanded, setLocalExpanded] = useState<number | false>(
    displayedRefNums[0] ?? 1,
  );
  const expanded =
    expandedReference !== undefined ? expandedReference : localExpanded;
  const setExpanded = (next: number | false) => {
    onExpandedChange?.(next);
    if (expandedReference === undefined) {
      setLocalExpanded(next);
    }
  };

  const getFieldsForRef = (n: number, type: "basic" | "address") => {
    const prefix = type === "basic" ? `basicReference${n}` : `addressReference${n}`;
    return fields.filter((f: any) =>
      String(f?.name || "").startsWith(prefix),
    );
  };

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const getCompletion = (sectionFields: any[]) => {
    const formValues = values || formikRef?.current?.values || {};
    const required = sectionFields.filter((f: any) => f?.required !== false);
    const filled = required.filter((f: any) => isFilled(formValues?.[f?.name])).length;
    return `${filled} / ${required.length}`;
  };

  return (
    <Box sx={{ width: "100%" , marginLeft: pageType === "SUMMARY" || isXsOrSm ? "8px" : 0,}}>
      {/* Reference accordions */}
      {displayedRefNums.map((n) => {
        const basicFields = getFieldsForRef(n, "basic");
        const addressFields = getFieldsForRef(n, "address");

        return (
          <Accordion
            key={n}
            expanded={expanded === n}
            onChange={() => setExpanded(expanded === n ? false : n)}
            sx={{
              mb: 2,
              border: "1px solid #e0e0e0",
              borderRadius: "8px !important",
              boxShadow: "none",
              minHeight:{ xs: "60px", sm: "70px" },
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600} fontSize="15px" marginTop={"5px"}>
                Reference {n}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ pl: 2, pr: 2 }}>
              {/* Basic Details */}
              {pageType==="FORM" && 
                <Box sx={{ mb: { xs: 0, sm: 2 } }}>
                  {isXsOrSm && <Divider sx={{ mb: {xs: 2, sm: 0 }}} />}
                  <SectionHeader
                    icon={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          borderRadius: "8px",
                          bgcolor: "#e5feffff",
                        }}
                      >
                        <img src={idIcon} alt="icon" style={{ width: 22, height: 22 }} />
                      </Box>
                    }
                    title="Basic Details"
                    subtitle=""
                    completion={getCompletion(basicFields)}
                  />
                </Box>
              }
              {
                pageType==="SUMMARY" && 
                <Grid item xs={12}>
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <SummarySectionHeader
                      title={"Basic Details"}
                    />
                  </Box>
                </Grid>
              }
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {basicFields.map((field: any) => (
                  <React.Fragment key={field?.id || field?.name}>
                    {renderField(field)}
                  </React.Fragment>
                ))}
              </Grid>

              {/* Address Details */}
              {pageType==="FORM" && 
                <Box sx={{ mb: { xs: 0, sm: 2 } }}>
                  {isXsOrSm && <Divider sx={{ mt: { xs: 2, sm: 0 }, mb: {xs: 2, sm: 0 }}} />}
                  <SectionHeader
                    icon={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          borderRadius: "8px",
                          bgcolor: "#e5feffff",
                        }}
                      >
                        <img src={locationIcon} alt="icon" style={{ width: 22, height: 22 }} />
                      </Box>
                    }
                    title="Address Details"
                    subtitle=""
                    completion={getCompletion(addressFields)}
                  />
                </Box>
              }
              {
                pageType==="SUMMARY" && 
                <Grid item xs={12}>
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <SummarySectionHeader
                      title={"Address Details"}
                    />
                  </Box>
                </Grid>
              }
              <Grid container spacing={2}>
                {addressFields.map((field: any) => (
                  <React.Fragment key={field?.id || field?.name}>
                    {renderField(field)}
                  </React.Fragment>
                ))}
              </Grid>

              {!hideInternalSaveButton && (
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-start" }}>
                  <PrimaryButton
                    type="button"
                    disabled={Boolean(isSaving)}
                    sx={{ width: { xs: "100%", sm: "auto" }}}
                    onClick={async () => {
                      try {
                        const result = onSaveAndNext
                          ? await onSaveAndNext(n)
                          : undefined;
                        if (result === false) return;
                        const nextIdx = displayedRefNums[displayedRefNums.indexOf(n) + 1];
                        if (nextIdx !== undefined) setExpanded(nextIdx);
                        else setExpanded(false);
                      } catch (error) {
                        // Save errors are handled by parent callbacks.
                      }
                    }}
                  >
                    {isSaving ? <>Save & Next<CircularProgress size={16} sx={{ color: "#fff", ml: 1 }} /></> : "Save & Next"}
                  </PrimaryButton>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Fallback if no reference fields loaded yet */}
      {displayedRefNums.length === 0 && (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No reference fields available.
        </Typography>
      )}
    </Box>
  );
};

export default ReferenceDetails;
